import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { useGetMyExamApplicationsQuery } from "../../api/examsApi";

const STATUS_COLORS: Record<string, string> = {
  draft:     "bg-gray-100 text-gray-500 border-gray-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-100",
  paid:      "bg-cyan-50 text-cyan-700 border-cyan-100",
  approved:  "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected:  "bg-rose-50 text-rose-700 border-rose-100",
  completed: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

function fmt(dt?: string | null) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return dt; }
}

/**
 * What this candidate still owes because the secretariat moved them to a dearer
 * exam, in kobo. 0 when there is nothing outstanding.
 *
 * The balance lives on its own invoice and is deliberately not counted among the
 * member's dues (an exam fee is not a subscription), so this card is where they
 * are told about it — there is nowhere else they would look.
 */
function balanceOwedKobo(app: any): number {
  const inv = app?.balance_invoice;
  if (!inv || inv.status === "paid") return 0;
  return Math.max(0, Number(inv.balance ?? 0));
}

const naira = (kobo: number) => `₦${(kobo / 100).toLocaleString("en-NG")}`;

export default function MyExamApplicationsPage() {
  const { data = [], isLoading, error, refetch } = useGetMyExamApplicationsQuery();
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return data;
    return data.filter((a) => {
      const t = `${a.exam?.title ?? ""} ${a.exam?.code ?? ""} ${a.diet?.title ?? ""}`.toLowerCase();
      return t.includes(s);
    });
  }, [data, q]);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-slate-900 uppercase tracking-widest">My Exam Applications</h1>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
            {data.length} application{data.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Link
          to="/exams/calls"
          className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
        >
          Apply for Exam
        </Link>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by exam or diet..."
          className="w-full md:w-96 border border-slate-200 rounded-xl px-4 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"
        />
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
        >
          Refresh
        </button>
      </div>

      {/* States */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      )}

      {error && (
        <div className="text-sm text-rose-600 font-bold">
          Failed to load.{" "}
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
          <p className="text-slate-400 font-black uppercase tracking-widest text-sm mb-3">
            {q ? "No matching applications" : "No applications yet"}
          </p>
          {!q && (
            <Link to="/exams/calls" className="px-5 py-2.5 bg-slate-900 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all">
              Browse Open Exams
            </Link>
          )}
        </div>
      )}

      {/* Application cards */}
      {!isLoading && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((app) => {
            const excludedCodes = ["MRI", "PCI"];
            const canDownload =
              !["draft", "rejected"].includes(app.status) &&
              !excludedCodes.includes((app.exam?.code ?? "").toUpperCase());
            return (
              <div key={app.id} className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-slate-900 text-sm truncate">{app.exam?.title || "Exam"}</p>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">{app.exam?.code}</p>
                  </div>
                  <span className={`flex-shrink-0 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${STATUS_COLORS[app.status] ?? "bg-gray-100 text-gray-500 border-gray-200"}`}>
                    {app.status}
                  </span>
                </div>

                <div className="text-xs text-slate-500 mb-4 space-y-0.5">
                  <p><span className="font-bold text-slate-700">Diet:</span> {app.diet?.title || "—"}</p>
                  <p><span className="font-bold text-slate-700">Centre:</span> {app.center?.name || "—"}</p>
                  {app.submitted_at && (
                    <p><span className="font-bold text-slate-700">Submitted:</span> {fmt(app.submitted_at)}</p>
                  )}
                </div>

                {/* Moved to a dearer exam and still owing the difference. Shown
                    before the buttons, in money terms, because it is the one
                    thing on this card the candidate has to act on. */}
                {balanceOwedKobo(app) > 0 && (
                  <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-orange-500">
                      Balance to pay
                    </p>
                    <p className="text-lg font-black text-slate-900 tracking-tight mt-0.5">
                      {naira(balanceOwedKobo(app))}
                    </p>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      Your registration was moved to this exam, which costs more than the one you
                      paid for. This is the difference only.
                    </p>
                    <Link
                      to={`/invoice/${(app as any).balance_invoice?.id}`}
                      className="inline-block mt-3 px-4 py-2 rounded-xl bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                    >
                      Pay Balance
                    </Link>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/exams/applications/${app.id}`}
                    className="flex-1 text-center py-2 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    View Details
                  </Link>
                  {canDownload && (
                    <Link
                      to={`/exams/applications/${app.id}/card`}
                      className="flex items-center justify-center gap-1.5 flex-1 py-2 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                    >
                      <Printer size={11} />
                      Admission Card
                    </Link>
                  )}
                  {(app as any).diet?.session?.id && (
                    <Link
                      to={`/exams/sessions/${(app as any).diet.session.id}/timetable`}
                      className="flex items-center justify-center gap-1.5 flex-1 py-2 rounded-xl bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                    >
                      <Printer size={11} />
                      Timetable
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
